import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, CheckCircle, Check, ArrowRight, Loader, Layers, Target, BrainCircuit, RotateCw, ClipboardCheck } from 'lucide-react';
// --- الدوال المساعدة ---
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// ==========================================
// 1. محرك المطابقة
// ==========================================
// ---------------------------------------------------------
// محرك المطابقة (النسخة الاحترافية - التصميم الخشبي الذكي)
// ---------------------------------------------------------
const MatchingGame = ({ pairs, onComplete }) => {
  const [gameCards, setGameCards] = useState([]);
  const [flippedIndices, setFlippedIndices] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [isShuffling, setIsShuffling] = useState(false);

  // خوارزمية التوزيع العشوائي (تعمل عند البداية وعند ضغط "إعادة اللعب")
  // خوارزمية التوزيع العشوائي (تم تصحيح التوافق الجيني مع استوديو الصناعة)
  const initializeGame = () => {
    setIsShuffling(true);
    let c = [];
    pairs.forEach(p => {
      // 1. قراءة الهيكلة الجديدة (القادمة من MatchingGameCreator الحالي)
      if (p.sideA !== undefined && p.sideB !== undefined) {
        if (p.sideA) c.push({ id: `${p.id}a`, pairId: p.id, content: p.sideA, type: p.sideAType });
        if (p.sideB) c.push({ id: `${p.id}b`, pairId: p.id, content: p.sideB, type: p.sideBType });
      } 
      // 2. حماية رجعية: قراءة الهيكلة القديمة (إن كان النشاط مصمماً بنسخة قديمة)
      else if (p.item1 && p.item2) {
        if (p.item1.content) c.push({ id: `${p.id}a`, pairId: p.id, ...p.item1 });
        if (p.item2.content) c.push({ id: `${p.id}b`, pairId: p.id, ...p.item2 });
      }
    });
    
    // خلط الأوراق عشوائياً
    setGameCards(c.sort(() => Math.random() - 0.5));
    setMatchedPairs([]);
    setFlippedIndices([]);
    setTimeout(() => setIsShuffling(false), 800);
  };

  useEffect(() => {
    if (pairs && pairs.length > 0) initializeGame();
  }, [pairs]);

  // التحقق من الفوز
  const isWon = pairs && pairs.length > 0 && matchedPairs.length === pairs.length;

  const totalCards = gameCards.length;
  let gridClass = 'grid-cols-3 md:grid-cols-4';
  if (totalCards > 12) gridClass = 'grid-cols-4 md:grid-cols-6 lg:grid-cols-8';
  if (totalCards >= 24) gridClass = 'grid-cols-5 md:grid-cols-8 lg:grid-cols-10';

  if (!pairs || pairs.length === 0) return null;

  return (
    <div className="w-full h-full flex flex-col relative bg-[#1a202c] overflow-hidden p-4 md:p-8 items-center justify-center rounded-[2.5rem] shadow-2xl border-4 border-slate-800">
      
      {/* شاشة التحميل والخلط */}
      {isShuffling && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/90 backdrop-blur-md">
          <RotateCw className="w-12 h-12 text-amber-500 animate-spin mb-4" />
          <h3 className="text-amber-500 font-bold text-2xl animate-pulse italic drop-shadow-lg">جاري توزيع الأوراق عشوائياً...</h3>
        </div>
      )}

      {/* شاشة الفوز وإعادة اللعب */}
      {isWon && !isShuffling && (
        <div className="absolute inset-0 z-[60] flex flex-col items-center justify-center bg-slate-900/95 backdrop-blur-md">
          <div className="bg-white/10 p-12 rounded-[3rem] border border-white/20 text-center shadow-2xl flex flex-col items-center">
            <CheckCircle className="w-24 h-24 text-emerald-400 mb-6 drop-shadow-lg" />
            <h2 className="text-4xl font-black text-white mb-8 drop-shadow-lg tracking-tighter">أداء مذهل! اكتمل التحدي.</h2>
            <div className="flex flex-wrap justify-center gap-4">
              <button onClick={initializeGame} className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white rounded-full font-black shadow-xl transition-all hover:scale-105 active:scale-95 flex items-center gap-3 text-lg">
                <RotateCw size={24} /> إعادة اللعب بتوزيع جديد
              </button>
              <button onClick={onComplete} className="px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full font-black shadow-xl transition-all hover:scale-105 active:scale-95 flex items-center gap-3 text-lg">
                <Check size={24} /> إنهاء ومغادرة
              </button>
            </div>
          </div>
        </div>
      )}

      {/* الوعاء المتكيف للبطاقات */}
      <div className={`grid gap-2 md:gap-4 w-full max-w-7xl h-auto max-h-[80vh] ${gridClass} content-center justify-center mx-auto`}>
        {!isShuffling && gameCards.map((card, idx) => {
          const isF = flippedIndices.includes(idx) || matchedPairs.includes(card.pairId);
          const isM = matchedPairs.includes(card.pairId);
          return (
            <div key={idx} onClick={() => {
              if (flippedIndices.length === 2 || isF || isM) return;
              const nf = [...flippedIndices, idx];
              setFlippedIndices(nf);
              if (nf.length === 2) {
                if (gameCards[nf[0]].pairId === gameCards[nf[1]].pairId) {
                  setTimeout(() => { setMatchedPairs([...matchedPairs, card.pairId]); setFlippedIndices([]); }, 700);
                } else {
                  setTimeout(() => setFlippedIndices([]), 1000);
                }
              }
            }} 
            className={`relative aspect-[4/3] w-full transition-all duration-500 cursor-pointer ${isM ? 'opacity-0 scale-90 pointer-events-none' : 'hover:scale-105 hover:-translate-y-1'}`} 
            style={{transformStyle: 'preserve-3d'}}>
               
               <div className="w-full h-full absolute inset-0 transition-transform duration-700 shadow-xl rounded-xl" style={{transformStyle: 'preserve-3d', transform: isF ? 'rotateY(180deg)' : 'rotateY(0deg)'}}>
                  
                  {/* ظهر البطاقة الخشبي */}
                  <div className="absolute inset-0 bg-amber-600 rounded-xl border-2 md:border-4 border-amber-900 flex items-center justify-center overflow-hidden shadow-inner" style={{backfaceVisibility: 'hidden', backgroundImage: 'url("https://www.transparenttextures.com/patterns/wood-pattern.png")'}}>
                     <div className="absolute inset-0 bg-black/10"></div>
                     <span className="text-2xl md:text-4xl font-black text-amber-900/40 italic z-10">{idx + 1}</span>
                  </div>
     
                  {/* وجه البطاقة المكشوف */}
                  <div className="absolute inset-0 bg-white rounded-xl border-2 md:border-4 border-white flex items-center justify-center overflow-hidden shadow-inner" style={{backfaceVisibility: 'hidden', transform: 'rotateY(180deg)'}}>
                     {card.type === 'text' ? 
                       <span className="p-1 md:p-2 text-[9px] md:text-sm font-bold text-center text-slate-800 break-words leading-tight">{card.content}</span> : 
                       <img src={card.content} className="w-full h-full object-contain p-2" alt="محتوى البطاقة" />
                     }
                  </div>
               </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==========================================
// ==========================================
// 2. محرك الترتيب الزمني/السينمائي (Sequencing)
// ==========================================
const SequencingGame = ({ steps, onComplete }) => {
  const [slots, setSlots] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isWon, setIsWon] = useState(false);

  const initializeGame = () => {
    setIsWon(false);
    setSlots(new Array(steps.length).fill(null));
    setAvailableItems([...steps].sort(() => Math.random() - 0.5));
  };

  useEffect(() => { if (steps && steps.length > 0) initializeGame(); }, [steps]);

  const handleDrop = (index) => {
    if (!draggedItem) return;
    const newSlots = [...slots];
    if (newSlots[index]) setAvailableItems(p => [...p, newSlots[index]]);
    newSlots[index] = draggedItem;
    setSlots(newSlots);
    setAvailableItems(p => p.filter(item => item.id !== draggedItem.id));
    setDraggedItem(null);

    if (newSlots.every(s => s !== null)) {
      const isCorrect = newSlots.every((s, i) => s.id === steps[i].id);
      if (isCorrect) setIsWon(true);
      else setTimeout(() => initializeGame(), 1500);
    }
  };

  if (!steps || steps.length === 0) return null;

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-8 bg-slate-950 rounded-[2.5rem] relative overflow-hidden shadow-2xl border-4 border-slate-800">
      {isWon && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/95 backdrop-blur-md">
          <div className="bg-white/10 p-12 rounded-[3rem] border border-white/20 text-center shadow-2xl">
            <CheckCircle className="w-24 h-24 text-emerald-400 mb-6 mx-auto drop-shadow-lg" />
            <h2 className="text-4xl font-black text-white mb-8">ترتيب مثالي!</h2>
            <div className="flex justify-center gap-4">
              <button onClick={initializeGame} className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white rounded-full font-black flex items-center gap-2"><RotateCw size={20}/> إعادة اللعب</button>
              <button onClick={onComplete} className="px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full font-black flex items-center gap-2"><Check size={20}/> إنهاء ومغادرة</button>
            </div>
          </div>
        </div>
      )}
      
      <div className="w-full max-w-5xl bg-[#111827] p-8 rounded-[2rem] border-y-8 border-black shadow-2xl flex gap-4 items-center justify-center min-h-[180px] mb-12">
        {slots.map((s, i) => (
          <div key={i} onDragOver={e => e.preventDefault()} onDrop={() => handleDrop(i)} className={`w-32 aspect-[4/3] rounded-xl border-4 transition-all flex items-center justify-center overflow-hidden ${s ? 'border-amber-500 bg-white' : 'border-dashed border-slate-700 bg-slate-900/50'}`}>
            {!s ? <span className="text-slate-700 text-4xl font-black">{i + 1}</span> : (
              s.type === 'image' ? <img src={s.content} className="object-contain w-full h-full p-2" /> : <span className="p-2 text-xs font-black text-slate-900 text-center">{s.content}</span>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-4 justify-center max-w-5xl">
        {availableItems.map((c, i) => (
          <div key={i} draggable onDragStart={() => setDraggedItem(c)} className="w-32 aspect-[4/3] bg-white rounded-xl border-4 border-amber-600 shadow-xl overflow-hidden cursor-grab active:cursor-grabbing hover:scale-105 transition-all flex items-center justify-center">
            {c.type === 'image' ? <img src={c.content} className="object-contain p-2 w-full h-full pointer-events-none" /> : <div className="p-2 text-xs font-black text-slate-900 text-center pointer-events-none">{c.content}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ==========================================
// 3. محرك التصنيف المجموعي (Sorting)
// ==========================================
const SortingGame = ({ categories, onComplete }) => {
  const [progress, setProgress] = useState({});
  const [availableItems, setAvailableItems] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isWon, setIsWon] = useState(false);

  const initializeGame = () => {
    setIsWon(false);
    setProgress({});
    let allItems = [];
    categories.forEach(cat => {
      cat.items.forEach(item => allItems.push({ ...item, correctCategory: cat.id }));
    });
    setAvailableItems(allItems.sort(() => Math.random() - 0.5));
  };

  useEffect(() => { if (categories && categories.length > 0) initializeGame(); }, [categories]);

  const handleDrop = (categoryId) => {
    if (!draggedItem) return;
    if (draggedItem.correctCategory === categoryId) {
      setProgress(p => ({ ...p, [categoryId]: [...(p[categoryId] || []), draggedItem] }));
      setAvailableItems(p => p.filter(item => item.id !== draggedItem.id));
      if (availableItems.length === 1) setIsWon(true);
    }
    setDraggedItem(null);
  };

  if (!categories || categories.length === 0) return null;

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-8 bg-[#0f172a] rounded-[2.5rem] relative overflow-hidden shadow-2xl border-4 border-slate-800">
      {isWon && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/95 backdrop-blur-md">
          <div className="bg-white/10 p-12 rounded-[3rem] border border-white/20 text-center shadow-2xl">
            <CheckCircle className="w-24 h-24 text-emerald-400 mb-6 mx-auto" />
            <h2 className="text-4xl font-black text-white mb-8">تصنيف دقيق!</h2>
            <div className="flex justify-center gap-4">
              <button onClick={initializeGame} className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white rounded-full font-black flex items-center gap-2"><RotateCw size={20}/> إعادة اللعب</button>
              <button onClick={onComplete} className="px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full font-black flex items-center gap-2"><Check size={20}/> إنهاء ومغادرة</button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-12 h-44 flex items-center justify-center">
        {availableItems.length > 0 && (
          <div draggable onDragStart={() => setDraggedItem(availableItems[0])} className="w-56 aspect-[4/3] bg-white rounded-2xl border-4 border-amber-500 shadow-2xl cursor-grab active:cursor-grabbing flex items-center justify-center overflow-hidden hover:scale-110 transition-transform">
            {availableItems[0].type === 'image' ? <img src={availableItems[0].content} className="w-full h-full object-contain p-2 pointer-events-none" /> : <span className="p-4 text-xl font-black text-slate-900 text-center pointer-events-none">{availableItems[0].content}</span>}
          </div>
        )}
      </div>

      <div className="w-full max-w-6xl bg-slate-900/80 rounded-[3rem] border-4 border-slate-700 shadow-2xl overflow-hidden min-h-[300px] flex divide-x-2 divide-slate-700 flex-row-reverse">
        {categories.map(cat => (
          <div key={cat.id} onDragOver={e => e.preventDefault()} onDrop={() => handleDrop(cat.id)} className="flex-1 flex flex-col p-6 items-center hover:bg-slate-800/50 transition-colors">
             <div className="mb-6 py-2 px-6 bg-slate-800 rounded-full border border-slate-600 shadow-lg"><span className="text-sm font-black text-indigo-400">{cat.title}</span></div>
             <div className="flex flex-wrap gap-2 justify-center">
               {(progress[cat.id] || []).map((item, idx) => (
                 <div key={idx} className="w-20 aspect-[4/3] bg-white rounded-xl overflow-hidden flex items-center justify-center shadow-md border-2 border-indigo-100">
                   {item.type === 'image' ? <img src={item.content} className="object-contain p-1 w-full h-full" /> : <span className="text-[10px] font-black p-1 text-center text-slate-800">{item.content}</span>}
                 </div>
               ))}
             </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==========================================
// 4. محرك كشف الأسرار (Mystery Game)
// ==========================================
const MysteryGame = ({ data, onComplete }) => {
  const [revealedTiles, setRevealedTiles] = useState([]);
  const [choices, setChoices] = useState([]);
  const [isWon, setIsWon] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const initializeGame = () => {
    setIsWon(false);
    setRevealedTiles([]);
    setFeedback(null);
    if (data && data.correctAnswer) {
      const allChoices = [data.correctAnswer, ...(data.distractors || [])].filter(c => c.trim() !== '');
      setChoices(allChoices.sort(() => Math.random() - 0.5));
    }
  };

  useEffect(() => { initializeGame(); }, [data]);

  const handleChoice = (choice) => {
    if (choice === data.correctAnswer) {
      setIsWon(true);
      const allTiles = Array.from({length: data.tileCount || 16}, (_, i) => i);
      setRevealedTiles(allTiles);
    } else {
      setFeedback('error');
      setTimeout(() => setFeedback(null), 1000);
    }
  };

  if (!data || !data.image) return null;

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-8 bg-slate-950 rounded-[2.5rem] relative overflow-hidden shadow-2xl border-4 border-slate-800">
      {isWon && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/80 backdrop-blur-sm">
          <div className="bg-white/10 p-12 rounded-[3rem] border border-white/20 text-center shadow-2xl">
            <CheckCircle className="w-24 h-24 text-emerald-400 mb-6 mx-auto" />
            <h2 className="text-4xl font-black text-white mb-8">إجابة صحيحة! سر مكشوف.</h2>
            <div className="flex justify-center gap-4">
              <button onClick={initializeGame} className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white rounded-full font-black flex items-center gap-2"><RotateCw size={20}/> إعادة اللعب</button>
              <button onClick={onComplete} className="px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full font-black flex items-center gap-2"><Check size={20}/> إنهاء ومغادرة</button>
            </div>
          </div>
        </div>
      )}

      <div className={`relative w-full max-w-3xl aspect-video bg-black rounded-[2rem] border-[10px] border-slate-800 shadow-2xl overflow-hidden transition-all ${feedback === 'error' ? 'animate-shake border-red-500' : ''}`}>
        <img src={data.image} className="w-full h-full object-contain p-4" />
        <div className="absolute inset-0 grid gap-1 p-1" style={{ gridTemplateColumns: `repeat(${Math.ceil(Math.sqrt((data.tileCount || 16) * 1.6))}, 1fr)` }}>
          {[...Array(data.tileCount || 16)].map((_, i) => (
            <div 
              key={i} 
              onClick={() => !revealedTiles.includes(i) && setRevealedTiles(p => [...p, i])} 
              className={`bg-slate-700/90 backdrop-blur-xl border border-slate-600 rounded-lg cursor-pointer transition-all duration-700 flex items-center justify-center ${revealedTiles.includes(i) ? 'opacity-0 scale-150 pointer-events-none' : 'hover:bg-slate-600'}`}
            >
              <span className="text-white/20 font-black">{i + 1}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12 flex flex-wrap gap-6 justify-center">
        {choices.map((c, i) => (
          <button key={i} onClick={() => handleChoice(c)} className="px-10 py-4 bg-slate-800 hover:bg-blue-600 text-white rounded-full font-black text-xl shadow-xl transition-all hover:scale-105 active:scale-95 border-2 border-slate-700 hover:border-blue-400">
            {c}
          </button>
        ))}
      </div>
    </div>
  );
};
// ==========================================
// 5. المكون الرئيسي للعرض (Activity Viewer)
// ==========================================
const ActivityViewer = () => {
  const navigate = useNavigate();
  const [activityData, setActivityData] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [mode, setMode] = useState('elements');
  const handleStartGame = () => {
    setMode('play');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const clapAudio = useRef(new Audio('https://assets.mixkit.co/active_storage/sfx/2013/2013-preview.mp3'));

  useEffect(() => {
    const saved = localStorage.getItem('tamkeen_live_activity');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.elements) parsed.elements.sort((a, b) => a.y - b.y);
      setActivityData(parsed);
    }
  }, []);

const handleGameComplete = () => {
    setGameCompleted(true);
    // تشغيل صوت التصفيق
    if (clapAudio.current) {
      clapAudio.current.play().catch(e => console.log("Audio failed"));
    }
    
    // الانتقال لصفحة التقييم بعد ثانيتين من الفرحة
    setTimeout(() => {
      navigate('/evaluation');
    }, 2000);
  };
  const goToEvaluation = () => {
    setIsFullscreen(false);
    navigate('/evaluation');
  };

  if (!activityData) return (
    <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
      <Loader className="w-10 h-10 text-indigo-500 animate-spin mb-4" />
      <p className="font-bold text-slate-600">جاري تحميل مسار النشاط...</p>
    </div>
  );

  const gameData = activityData.template?.data?.[activityData.template.id] || activityData.template?.data;

return (
    <div className="h-screen w-screen flex flex-col bg-[#0f172a] text-slate-300 font-sans overflow-hidden direction-rtl text-right">
      <header className="h-16 bg-[#1e293b] border-b border-slate-800 flex items-center justify-between px-6 shrink-0 z-30 shadow-lg">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-700 rounded-full transition-all text-slate-400 hover:text-white">
            <ArrowRight className="w-5 h-5" />
          </button>
          <div className="w-px h-6 bg-slate-700 mx-1"></div>
          <div className="flex flex-col">
            <h1 className="text-sm font-bold text-white truncate w-64">{activityData?.title || "نشاط تعليمي"}</h1>
            <span className="text-[9px] text-slate-500 uppercase tracking-widest">تطبيق المستفيد</span>
          </div>
        </div>
        <button onClick={goToEvaluation} className="text-xs font-bold text-emerald-400 hover:text-white bg-emerald-950 hover:bg-emerald-700 px-4 py-2 rounded-full border border-emerald-800 hover:border-emerald-500 shadow-inner transition-all flex items-center gap-2">
         <ClipboardCheck className="w-4 h-4" /> إنهاء العرض والتقييم
       </button>
      </header>

      <div className="flex-1 overflow-auto bg-slate-900 p-8 flex justify-center custom-scrollbar relative">
        
        {/* المرحلة الأولى: الصفحة السردية (القصة/التقديم) */}
        {mode === 'elements' && (
          <div 
            className="shadow-[-10px_0_30px_rgba(0,0,0,0.4)] relative transition-colors duration-300 overflow-hidden rounded-md border border-white/10"
            style={{ 
              width: activityData?.pageSettings?.width || 800, 
              minHeight: activityData?.pageSettings?.height || 1000, 
              backgroundColor: activityData?.pageSettings?.backgroundColor || '#f8fafc' 
            }}
          >
            {activityData?.elements?.map((el) => (
              <div 
                key={el.id} 
                className="absolute"
                style={{ 
                  left: el.x, 
                  top: el.y, 
                  width: el.width, 
                  height: (el.type === 'text' && el.height === 'auto') ? 'auto' : el.height,
                  opacity: el.opacity, 
                  borderRadius: el.cornerRadius, 
                  transform: `rotate(${el.rotation || 0}deg)`, 
                  overflow: 'hidden' 
                }}
              >
                {el.type === 'text' && (
                  <p 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      fontSize: `${el.fontSize}px`, 
                      fontFamily: el.fontFamily || 'Cairo', 
                      color: el.color === '#ffffff' ? '#1e293b' : el.color,
                      textAlign: el.textAlign || 'center', 
                      fontWeight: el.fontWeight || 'bold', 
                      padding: '8px', 
                      wordWrap: 'break-word',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {el.content}
                  </p>
                )}
                {el.type === 'image' && <img src={el.src || el.content} alt="وسائط" className="w-full h-full pointer-events-none" style={{ objectFit: 'fill' }} />}
                {el.type === 'video' && <video src={el.src || el.content} controls className="w-full h-full bg-black object-contain" />}
                {el.type === 'audio' && (
                  <div className="w-full h-full bg-slate-100 flex items-center justify-center p-2 rounded-lg">
                    <audio src={el.src || el.content} controls className="w-full" />
                  </div>
                )}
              </div>
            ))}
            
            {/* بوابة المختبر التفاعلي */}
           {/* 1. بوابة المختبر التفاعلي (إذا كان هناك لعبة مدمجة) */}
            {activityData?.template && (
              <div 
                className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 p-8 bg-white/5 backdrop-blur-xl rounded-[2.5rem] border border-white/10 shadow-2xl z-20 text-center"
                style={{ bottom: 100 }}
              >
                <div className="p-3 bg-amber-500/10 rounded-full border border-amber-500/20"><BrainCircuit className="w-8 h-8 text-amber-400 animate-pulse" /></div>
                <h3 className="text-xl font-black text-white italic drop-shadow-lg">تم اكتشاف نشاط تفاعلي!</h3>
                <p className="text-slate-400 text-xs font-bold max-w-sm leading-relaxed">أكملت المرحلة السردية بنجاح، هل أنت جاهز للانتقال إلى المختبر لتطبيق ما تعلمته في تحدي <b>({activityData.template.name})</b>؟</p>
                <button onClick={handleStartGame} className="mt-2 px-10 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white rounded-full font-black text-sm shadow-xl transition-all transform hover:scale-105 active:scale-95">ابدأ التحدي الآن</button>              
              </div>
            )}

            {/* 2. بوابة التقييم المباشر (للأنشطة السردية النقية التي لا تحتوي على لعبة) */}
            {!activityData?.template && (
              <div 
                className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 p-8 bg-white/5 backdrop-blur-xl rounded-[2.5rem] border border-white/10 shadow-2xl z-20 text-center"
                style={{ bottom: 100 }}
              >
                <div className="p-3 bg-emerald-500/10 rounded-full border border-emerald-500/20"><CheckCircle className="w-8 h-8 text-emerald-400 animate-bounce" /></div>
                <h3 className="text-xl font-black text-white italic drop-shadow-lg">نهاية النشاط السردي</h3>
                <p className="text-slate-400 text-xs font-bold max-w-sm leading-relaxed">لقد اكتمل عرض محتوى النشاط بنجاح. يمكنك الآن الانتقال لتقييم استجابة المستفيدين.</p>
                <button onClick={handleGameComplete} className="mt-2 px-10 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white rounded-full font-black text-sm shadow-xl transition-all transform hover:scale-105 active:scale-95">
                  الانتقال للتقييم
                </button>              
              </div>
            )}
          </div>
        )}

        {/* المرحلة الثانية: المختبر التفاعلي المدمج */}
        {mode === 'play' && activityData?.template && (
          <div className="absolute inset-0 z-50 flex-1 overflow-auto bg-slate-950 flex justify-center custom-scrollbar direction-rtl">
            <button onClick={() => setMode('elements')} className="fixed top-20 right-6 z-50 p-3 bg-slate-800 hover:bg-red-500/20 rounded-full text-slate-400 hover:text-red-400 transition-colors shadow-2xl border border-slate-700">
              <X className="w-5 h-5" />
            </button>
            
           {activityData.template.type === 'matching' && <MatchingGame pairs={gameData?.pairs || (Array.isArray(gameData) ? gameData : [])} onComplete={handleGameComplete} />}
            {activityData.template.type === 'sequencing' && <SequencingGame steps={gameData?.steps || (Array.isArray(gameData) ? gameData : [])} onComplete={handleGameComplete} />}
            {activityData.template.type === 'sorting' && <SortingGame categories={gameData?.categories || (Array.isArray(gameData) ? gameData : [])} onComplete={handleGameComplete} />}
            {activityData.template.type === 'mystery' && <MysteryGame data={gameData || {}} onComplete={handleGameComplete} />}
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 20px; }
        .direction-rtl { direction: rtl; }
      `}} />
    </div>
  );
  };
export default ActivityViewer;